import axios, { AxiosInstance } from 'axios';
import {
  FilterParams,
  QueryResponse,
  ErrorRateData,
  CostByCompanyData,
  PerformanceMetrics,
  UserActivity,
  AIProcessingTimeData
} from '../types/audit';

// No fixed production default: the backend API has no custom domain (see
// cdk/lib/backend-private-api-stack.ts), so its URL is only known after deploy. deploy.sh writes
// REACT_APP_API_ENDPOINT into .env.production.local from the CDK stack output before building.
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';

export interface CompanyOption {
  id: string;
  name: string;
}

export type Environment = 'dev' | 'prod';

class AuditAPIService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_ENDPOINT,
      timeout: 120000, // 120 seconds for Athena queries
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get all companies (id + name) from MongoDB
   */
  async getCompanies(env: Environment = 'prod'): Promise<CompanyOption[]> {
    const response = await this.client.get('/companies', { params: { environment: env } });
    return response.data.data;
  }

  /**
   * Query audit logs with filters
   */
  async queryAudits(filters: FilterParams, env: Environment = 'prod'): Promise<QueryResponse> {
    const response = await this.client.post('/query', {
      queryType: 'audit_logs',
      filters,
      environment: env,
    });
    return response.data;
  }

  /**
   * Query by report type (quick reports)
   */
  async queryByType(queryType: string, filters: FilterParams, env: Environment = 'prod'): Promise<QueryResponse> {
    const response = await this.client.post('/query', {
      queryType,
      filters,
      environment: env,
    });
    return response.data;
  }

  /**
   * Get error rate over time
   */
  async getErrorRate(startDate: string, endDate: string, env: Environment = 'prod'): Promise<ErrorRateData[]> {
    const response = await this.client.post('/query', {
      queryType: 'error_rate',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get cost breakdown by company
   */
  async getCostByCompany(startDate: string, endDate: string, env: Environment = 'prod'): Promise<CostByCompanyData[]> {
    const response = await this.client.post('/query', {
      queryType: 'cost_by_company',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get performance metrics by endpoint
   */
  async getPerformanceMetrics(startDate: string, endDate: string, env: Environment = 'prod'): Promise<PerformanceMetrics[]> {
    const response = await this.client.post('/query', {
      queryType: 'performance_metrics',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get most active users
   */
  async getUserActivity(startDate: string, endDate: string, limit: number = 20, env: Environment = 'prod'): Promise<UserActivity[]> {
    const response = await this.client.post('/query', {
      queryType: 'user_activity',
      filters: {
        dateRange: { start: startDate, end: endDate },
        limit,
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get requests by country and locale
   */
  async getGeographicDistribution(startDate: string, endDate: string, env: Environment = 'prod'): Promise<any[]> {
    const response = await this.client.post('/query', {
      queryType: 'geographic_distribution',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get daily token usage trends
   */
  async getTokenUsage(startDate: string, endDate: string, env: Environment = 'prod'): Promise<any[]> {
    const response = await this.client.post('/query', {
      queryType: 'token_usage',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get slowest requests
   */
  async getSlowRequests(date: string, limit: number = 20, env: Environment = 'prod'): Promise<any[]> {
    const response = await this.client.post('/query', {
      queryType: 'slow_requests',
      filters: {
        dateRange: { start: date, end: date },
        limit,
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Export data as CSV
   */
  async exportToCSV(filters: FilterParams, env: Environment = 'prod'): Promise<Blob> {
    const response = await this.client.post('/export', {
      format: 'csv',
      filters,
      environment: env,
    }, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export data as JSON
   */
  async exportToJSON(filters: FilterParams, env: Environment = 'prod'): Promise<Blob> {
    const response = await this.client.post('/export', {
      format: 'json',
      filters,
      environment: env,
    }, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Get AI processing time distribution for box plot
   * Returns all requests in the date range (no limit)
   */
  async getAIProcessingTimeDistribution(startDate: string, endDate: string, env: Environment = 'prod'): Promise<AIProcessingTimeData[]> {
    const response = await this.client.post('/query', {
      queryType: 'ai_processing_time_distribution',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get response time histogram (bucketed)
   */
  async getResponseTimeHistogram(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{timeBucket: string; requestCount: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'response_time_histogram',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get avg response time by processedCount (POST requests only)
   */
  async getAvgTimeByRequestSize(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{processedCount: number; requestCount: number; avgTimeMs: number; medianTimeMs: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'avg_time_by_request_size',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Get POST vs PATCH request counts per company
   */
  async getPostVsPatchByCompany(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{companyId: string; companyName?: string; postCount: number; patchCount: number; totalCount: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'post_vs_patch_by_company',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Chart 1.0: Orders sent per company, distinct orderId count
   */
  async getOrdersSentByCompanyDistinct(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{companyId: string; companyName?: string; distinctOrderCount: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'orders_sent_by_company_distinct',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Chart 1.2: Orders sent per company, counting every send event (no dedup)
   */
  async getOrdersSentByCompanyAll(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{companyId: string; companyName?: string; totalSentCount: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'orders_sent_by_company_all',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }

  /**
   * Chart 2: Repairs disabled vs sent per company (distinct orderId+repairId pairs)
   */
  async getRepairsDisabledVsSentByCompany(startDate: string, endDate: string, env: Environment = 'prod'): Promise<{companyId: string; companyName?: string; disabledCount: number; sentCount: number}[]> {
    const response = await this.client.post('/query', {
      queryType: 'repairs_disabled_vs_sent_by_company',
      filters: {
        dateRange: { start: startDate, end: endDate },
      },
      environment: env,
    });
    return response.data.data;
  }
}

export const auditAPI = new AuditAPIService();
export default auditAPI;

